import React, { useState, useEffect, useContext, useCallback } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import { UserContext } from "../contexts/UserContext";
import ComisionModal from "./ComisionModal";

const BASE = process.env.REACT_APP_API_BASE || "http://localhost/twinpack/php";

const fmt = (n) => "$" + (parseFloat(n) || 0).toLocaleString("es-AR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtFecha = (s) => {
  if (!s) return "—";
  const d = new Date(s);
  if (isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("es-AR", { day: "2-digit", month: "2-digit", year: "numeric" });
};

// Rango de fechas según período
const rangoPeriodo = (periodo) => {
  const hoy = new Date();
  const y = hoy.getFullYear();
  const m = hoy.getMonth();
  const iso = (dt) => dt.toISOString().slice(0, 10);
  switch (periodo) {
    case "este_mes":     return { desde: iso(new Date(y, m, 1)),     hasta: iso(new Date(y, m + 1, 0)) };
    case "mes_pasado":   return { desde: iso(new Date(y, m - 1, 1)), hasta: iso(new Date(y, m, 0)) };
    case "trimestre": {
      const qStart = Math.floor(m / 3) * 3;
      return { desde: iso(new Date(y, qStart, 1)), hasta: iso(new Date(y, qStart + 3, 0)) };
    }
    default: return { desde: null, hasta: null }; // todo / personalizado se manejan aparte
  }
};

const S = {
  page: { minHeight: "100vh", background: "#f3f0f4", padding: "28px 20px", fontFamily: "'Segoe UI', system-ui, sans-serif" },
  header: { maxWidth: 1200, margin: "0 auto 20px", display: "flex", alignItems: "flex-end", justifyContent: "space-between", flexWrap: "wrap", gap: 12 },
  title: { fontSize: 22, fontWeight: 700, color: "#121212", margin: 0 },
  subtitle: { fontSize: 13, color: "#888", marginTop: 2 },
  btnNew: { padding: "9px 18px", background: "#9f0059", color: "#fff", border: "none", borderRadius: 8, fontSize: 14, fontWeight: 700, cursor: "pointer" },
  filters: { maxWidth: 1200, margin: "0 auto 20px", display: "flex", flexWrap: "wrap", gap: 10, alignItems: "center" },
  select: { padding: "8px 12px", border: "1.5px solid #e0d6dc", borderRadius: 8, fontSize: 13, color: "#333", background: "#fff", outline: "none", cursor: "pointer" },
  btnMine: (on) => ({ padding: "8px 14px", borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: "pointer", border: "1.5px solid " + (on ? "#9f0059" : "#e0d6dc"), background: on ? "#fdf0f6" : "#fff", color: on ? "#9f0059" : "#666" }),
  dateInput: { padding: "7px 10px", border: "1.5px solid #e0d6dc", borderRadius: 8, fontSize: 13, color: "#333", outline: "none" },
  kpiRow: { maxWidth: 1200, margin: "0 auto 20px", display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 14 },
  kpiCard: (color) => ({ background: "#fff", borderRadius: 14, padding: "16px 20px", boxShadow: "0 1px 8px rgba(0,0,0,0.06)", borderLeft: `4px solid ${color}` }),
  kpiLabel: { fontSize: 11, fontWeight: 700, color: "#aaa", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 6 },
  kpiValue: { fontSize: 24, fontWeight: 800, color: "#121212" },
  tableWrap: { maxWidth: 1200, margin: "0 auto", background: "#fff", borderRadius: 14, overflow: "hidden", boxShadow: "0 1px 8px rgba(0,0,0,0.06)" },
  th: { background: "#9f0059", color: "#fff", padding: "11px 14px", fontSize: 11, fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", textAlign: "left", whiteSpace: "nowrap" },
  td: { padding: "11px 14px", fontSize: 13, color: "#222", borderBottom: "1px solid #f5f0f3", verticalAlign: "middle" },
  badge: (pagada) => ({ display: "inline-block", padding: "3px 10px", borderRadius: 12, fontSize: 11, fontWeight: 700, background: pagada ? "#dcfce7" : "#fef3c7", color: pagada ? "#16a34a" : "#a16207" }),
  iconBtn: { background: "none", border: "none", cursor: "pointer", fontSize: 13, padding: "3px 6px", borderRadius: 5, fontWeight: 600 },
  empty: { textAlign: "center", padding: "48px", color: "#aaa", fontSize: 14 },
};

const Comisiones = () => {
  const { user } = useContext(UserContext);
  const userId = user?.id || JSON.parse(localStorage.getItem("user") || "{}").id;

  const [data,    setData]    = useState({ comisiones: [], totales: { pendiente: 0, pagado: 0, total: 0 } });
  const [loading, setLoading] = useState(true);
  const [usuarios, setUsuarios] = useState([]);

  // Filtros
  const [fUsuario, setFUsuario] = useState("");
  const [fPeriodo, setFPeriodo] = useState("este_mes");
  const [fEstado,  setFEstado]  = useState("todas");
  const [persDesde, setPersDesde] = useState("");
  const [persHasta, setPersHasta] = useState("");

  // Modal
  const [modal, setModal] = useState(null); // null | {} (nueva) | comision (editar)

  useEffect(() => {
    axios.post(`${BASE}/buscar_usuarios.php`)
      .then((r) => setUsuarios((Array.isArray(r.data) ? r.data : []).filter((u) => Number(u.comercial) === 1 && Number(u.activo) === 1)))
      .catch(() => {});
  }, []);

  const cargar = useCallback(() => {
    setLoading(true);
    let desde = null, hasta = null;
    if (fPeriodo === "personalizado") { desde = persDesde || null; hasta = persHasta || null; }
    else if (fPeriodo !== "todo")     { const r = rangoPeriodo(fPeriodo); desde = r.desde; hasta = r.hasta; }

    const params = new URLSearchParams({ estado_pago: fEstado });
    if (fUsuario)  params.append("usuario_id", fUsuario);
    if (desde)     params.append("fecha_desde", desde);
    if (hasta)     params.append("fecha_hasta", hasta);

    axios.post(`${BASE}/comisiones_listar.php`, params)
      .then((r) => {
        if (r.data?.success) setData({ comisiones: r.data.comisiones, totales: r.data.totales });
        else setData({ comisiones: [], totales: { pendiente: 0, pagado: 0, total: 0 } });
      })
      .catch(() => setData({ comisiones: [], totales: { pendiente: 0, pagado: 0, total: 0 } }))
      .finally(() => setLoading(false));
  }, [fUsuario, fPeriodo, fEstado, persDesde, persHasta]);

  useEffect(() => { cargar(); }, [cargar]);

  const togglePagada = async (c) => {
    try {
      const r = await axios.post(`${BASE}/comisiones_marcar_pagada.php`, new URLSearchParams({ id: c.id, pagada: c.pagada ? 0 : 1 }));
      if (r.data?.success) cargar();
      else toast.error("Error");
    } catch { toast.error("Error de conexión"); }
  };

  const eliminar = async (c) => {
    if (!window.confirm(`¿Eliminar la comisión de ${c.usuario_nombre} sobre la OC #${c.orden_id}?`)) return;
    try {
      const r = await axios.post(`${BASE}/comisiones_eliminar.php`, new URLSearchParams({ id: c.id }));
      if (r.data?.success) { toast.success("Comisión eliminada"); cargar(); }
      else toast.error("Error");
    } catch { toast.error("Error de conexión"); }
  };

  const soloLoMio = fUsuario && String(fUsuario) === String(userId);

  return (
    <div style={S.page}>
      {/* Header */}
      <div style={S.header}>
        <div>
          <h2 style={S.title}>Comisiones</h2>
          <p style={S.subtitle}>{data.comisiones.length} comisión{data.comisiones.length !== 1 ? "es" : ""} en el filtro actual</p>
        </div>
        <button style={S.btnNew} onClick={() => setModal({})}>+ Nueva comisión</button>
      </div>

      {/* Filtros */}
      <div style={S.filters}>
        <select style={S.select} value={fUsuario} onChange={(e) => setFUsuario(e.target.value)}>
          <option value="">Todos los usuarios</option>
          {usuarios.map((u) => <option key={u.id} value={u.id}>{u.nombre}</option>)}
        </select>
        <button style={S.btnMine(soloLoMio)} onClick={() => setFUsuario(soloLoMio ? "" : String(userId))}>
          Solo lo mío
        </button>
        <select style={S.select} value={fPeriodo} onChange={(e) => setFPeriodo(e.target.value)}>
          <option value="todo">Todo el período</option>
          <option value="este_mes">Este mes</option>
          <option value="mes_pasado">Mes pasado</option>
          <option value="trimestre">Este trimestre</option>
          <option value="personalizado">Personalizado</option>
        </select>
        {fPeriodo === "personalizado" && (
          <>
            <input type="date" style={S.dateInput} value={persDesde} onChange={(e) => setPersDesde(e.target.value)} />
            <span style={{ color: "#888", fontSize: 13 }}>a</span>
            <input type="date" style={S.dateInput} value={persHasta} onChange={(e) => setPersHasta(e.target.value)} />
          </>
        )}
        <select style={S.select} value={fEstado} onChange={(e) => setFEstado(e.target.value)}>
          <option value="todas">Todas</option>
          <option value="pendientes">Pendientes</option>
          <option value="pagadas">Pagadas</option>
        </select>
      </div>

      {/* KPIs */}
      <div style={S.kpiRow}>
        <div style={S.kpiCard("#f59e0b")}>
          <p style={S.kpiLabel}>Pendiente</p>
          <p style={S.kpiValue}>{fmt(data.totales.pendiente)}</p>
        </div>
        <div style={S.kpiCard("#16a34a")}>
          <p style={S.kpiLabel}>Pagado</p>
          <p style={S.kpiValue}>{fmt(data.totales.pagado)}</p>
        </div>
        <div style={S.kpiCard("#9f0059")}>
          <p style={S.kpiLabel}>Total</p>
          <p style={S.kpiValue}>{fmt(data.totales.total)}</p>
        </div>
      </div>

      {/* Tabla */}
      {loading ? (
        <p style={{ textAlign: "center", color: "#aaa", padding: 40 }}>Cargando…</p>
      ) : data.comisiones.length === 0 ? (
        <div style={S.tableWrap}><p style={S.empty}>No hay comisiones en el filtro actual.</p></div>
      ) : (
        <div style={S.tableWrap}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr>
                {["Cliente", "N° OC", "Fecha entrega", "Monto OC", "%", "Comisión", "Comisionado", "Estado", "Acciones"].map((h) => (
                  <th key={h} style={S.th}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.comisiones.map((c, i) => (
                <tr key={c.id} style={{ background: i % 2 === 0 ? "#fff" : "#fdf8fb" }}>
                  <td style={{ ...S.td, fontWeight: 600 }}>{c.cliente || "—"}</td>
                  <td style={S.td}>#{c.orden_id}</td>
                  <td style={S.td}>{fmtFecha(c.fecha_entrega)}</td>
                  <td style={S.td}>{fmt(c.monto_oc)}</td>
                  <td style={S.td}>{parseFloat(c.porcentaje)}%</td>
                  <td style={{ ...S.td, fontWeight: 700 }}>{fmt(c.monto_comision)}</td>
                  <td style={S.td}>{c.usuario_nombre || "—"}</td>
                  <td style={S.td}><span style={S.badge(c.pagada)}>{c.pagada ? "Pagada" : "Pendiente"}</span></td>
                  <td style={{ ...S.td, whiteSpace: "nowrap" }}>
                    <button style={{ ...S.iconBtn, color: c.pagada ? "#a16207" : "#16a34a" }} onClick={() => togglePagada(c)} title={c.pagada ? "Marcar pendiente" : "Marcar pagada"}>
                      {c.pagada ? "↩ Pendiente" : "✓ Pagar"}
                    </button>
                    <button style={{ ...S.iconBtn, color: "#555" }} onClick={() => setModal(c)} title="Editar">✏️</button>
                    <button style={{ ...S.iconBtn, color: "#dc2626" }} onClick={() => eliminar(c)} title="Eliminar">🗑</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {modal !== null && (
        <ComisionModal
          comision={Object.keys(modal).length ? modal : null}
          userId={userId}
          onClose={() => setModal(null)}
          onSaved={() => { setModal(null); cargar(); }}
        />
      )}
    </div>
  );
};

export default Comisiones;
